import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '../stores/useAppStore';
import { useUsage } from '../stores/useUsage';
import { fmtBytes, fmtNum, fmtUsd } from '../lib/costs';
import {
  computeStorageStats,
  type StorageStats,
} from '../lib/storageStats';
import { SyncError, syncMe } from '../lib/syncClient';
import { setTokenCookie, clearTokenCookie } from '../lib/authToken';
import { rescueToServer, type RescueProgress } from '../lib/rescue';
import {
  bootstrapFromServer,
  type BootstrapProgress,
} from '../lib/bootstrap';
import './routes.css';

const DEFAULT_API_BASE_URL =
  typeof window === 'undefined'
    ? ''
    : window.location.origin +
      (import.meta.env.BASE_URL || '/').replace(/\/+$/, '') +
      '/api';

const ANTHROPIC_MODEL_OPTIONS: { id: string; label: string }[] = [
  { id: 'claude-opus-4-7', label: 'Opus 4.7 — most capable' },
  { id: 'claude-opus-4-6', label: 'Opus 4.6' },
  { id: 'claude-sonnet-4-6', label: 'Sonnet 4.6 — balanced' },
  { id: 'claude-haiku-4-5', label: 'Haiku 4.5 — fastest, cheapest' },
];

const TTS_MODEL_OPTIONS: { id: string; label: string }[] = [
  { id: 'eleven_v3', label: 'v3 — most expressive (slow)' },
  { id: 'eleven_multilingual_v2', label: 'Multilingual v2' },
  { id: 'eleven_turbo_v2_5', label: 'Turbo v2.5 — faster, cheaper' },
  { id: 'eleven_flash_v2_5', label: 'Flash v2.5 — fastest, cheapest' },
];

export default function SettingsRoute() {
  const settings = useAppStore((s) => s.settings);
  const setSettings = useAppStore((s) => s.setSettings);
  const usage = useUsage();
  const resetUsage = useUsage((s) => s.reset);
  const nav = useNavigate();

  const [stats, setStats] = useState<StorageStats | undefined>();
  const [statsBusy, setStatsBusy] = useState(false);

  const [testStatus, setTestStatus] = useState<
    | { kind: 'idle' }
    | { kind: 'busy' }
    | { kind: 'ok'; user: string }
    | { kind: 'err'; message: string }
  >({ kind: 'idle' });

  const [rescueBusy, setRescueBusy] = useState(false);
  const [rescueProgress, setRescueProgress] = useState<
    RescueProgress | BootstrapProgress | null
  >(null);
  const [rescueLog, setRescueLog] = useState<string[]>([]);
  const rescueLogRef = useRef<HTMLDivElement | null>(null);
  const [pullBusy, setPullBusy] = useState(false);

  function appendLog(line: string) {
    setRescueLog((prev) => [...prev, line]);
    queueMicrotask(() => {
      const el = rescueLogRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }

  async function onTestConnection() {
    setTestStatus({ kind: 'busy' });
    try {
      const res = await syncMe();
      setTestStatus({ kind: 'ok', user: res.user });
    } catch (e) {
      const msg = e instanceof SyncError ? e.message : (e as Error).message;
      setTestStatus({ kind: 'err', message: msg });
    }
  }

  async function onRescue() {
    if (
      !confirm(
        'Upload all local stories, audio, prose, outlines, chats, and metadata to the server? Local copies are kept; this only copies them up.'
      )
    ) {
      return;
    }
    setRescueBusy(true);
    setRescueLog([]);
    setRescueProgress(null);
    try {
      await rescueToServer({
        onProgress: (p) => setRescueProgress(p),
        onLog: appendLog,
      });
      setRescueProgress(null);
    } catch (e) {
      const msg = e instanceof SyncError ? e.message : (e as Error).message;
      appendLog(`Aborted: ${msg}`);
    } finally {
      setRescueBusy(false);
      void refreshStats();
    }
  }

  async function onPull() {
    if (
      !confirm(
        'Pull universes, stories, slugs, chats, queue, and settings from the server? This overwrites the corresponding local state.'
      )
    ) {
      return;
    }
    setPullBusy(true);
    setRescueLog([]);
    setRescueProgress(null);
    try {
      const r = await bootstrapFromServer({
        onProgress: (p) => setRescueProgress(p),
        onLog: appendLog,
      });
      appendLog(
        `Done. ${r.universes} universes, ${r.stories} stories, ${r.slugs} slugs, ${r.chats} chats.`
      );
      setRescueProgress(null);
    } catch (e) {
      const msg = e instanceof SyncError ? e.message : (e as Error).message;
      appendLog(`Aborted: ${msg}`);
    } finally {
      setPullBusy(false);
      void refreshStats();
    }
  }

  async function refreshStats() {
    setStatsBusy(true);
    try {
      const s = await computeStorageStats();
      setStats(s);
    } finally {
      setStatsBusy(false);
    }
  }

  useEffect(() => {
    void refreshStats();
  }, []);

  function onResetUsage() {
    if (!confirm('Reset all cost & usage stats? This does not affect the API itself.')) return;
    resetUsage();
  }

  const haveQuota =
    !!stats &&
    stats.estimateQuota != null &&
    stats.estimateQuota > 0 &&
    stats.estimateUsage != null;
  const quotaPct = haveQuota
    ? Math.min(100, (stats!.estimateUsage! / stats!.estimateQuota!) * 100)
    : undefined;

  const ourBytes =
    (stats?.audio.bytes ?? 0) +
    (stats?.prose.bytes ?? 0) +
    (stats?.outlines.bytes ?? 0) +
    (stats?.localStorageBytes ?? 0);

  // Stacked bar percentages: audio dominates, others tiny. Compute against the
  // bigger of (browser usage estimate) or (our own sum) so segments are
  // proportional even if estimate is missing.
  const stackTotal = Math.max(stats?.estimateUsage ?? 0, ourBytes, 1);
  const audioPct = stats ? (stats.audio.bytes / stackTotal) * 100 : 0;
  const textPct = stats
    ? ((stats.prose.bytes + stats.outlines.bytes) / stackTotal) * 100
    : 0;
  const lsPct = stats ? (stats.localStorageBytes / stackTotal) * 100 : 0;

  return (
    <div className="route">
      <h1>Settings</h1>
      <div className="dim small">
        Global settings. Voice, prompts, and per-world tone live in each universe's settings.
      </div>

      <section className="card">
        <h2>Sync</h2>
        <div className="dim small">
          Server-side storage on armagnac. Token + base URL are stored only on this
          device. Leave the URL blank to use the default (same origin as the PWA).
        </div>
        <label>
          <div className="label">Sync API base URL</div>
          <input
            type="text"
            value={settings.apiBaseUrl ?? ''}
            onChange={(e) => setSettings({ apiBaseUrl: e.target.value })}
            autoComplete="off"
            placeholder={DEFAULT_API_BASE_URL || '(default: same origin + /api)'}
          />
        </label>
        <label>
          <div className="label">Sync token</div>
          <input
            type="password"
            value={settings.apiToken ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setSettings({ apiToken: v });
              if (v.trim()) setTokenCookie(v.trim());
              else clearTokenCookie();
            }}
            autoComplete="off"
            placeholder="tok_…"
          />
        </label>
        <div className="row">
          <button
            onClick={onTestConnection}
            disabled={!settings.apiToken || testStatus.kind === 'busy'}
          >
            {testStatus.kind === 'busy' ? 'Testing…' : 'Test connection'}
          </button>
          {testStatus.kind === 'ok' && (
            <span className="dim small" style={{ marginLeft: 8 }}>
              Authed as <strong>{testStatus.user}</strong>.
            </span>
          )}
          {testStatus.kind === 'err' && (
            <span className="dim small" style={{ marginLeft: 8, color: '#f08080' }}>
              {testStatus.message}
            </span>
          )}
        </div>

        <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: 'wrap' }}>
          <button
            onClick={onRescue}
            disabled={!settings.apiToken || rescueBusy || pullBusy}
          >
            {rescueBusy ? 'Rescuing…' : 'Rescue local data to server'}
          </button>
          <button
            onClick={onPull}
            disabled={!settings.apiToken || rescueBusy || pullBusy}
          >
            {pullBusy ? 'Pulling…' : 'Pull from server'}
          </button>
        </div>
        {rescueProgress && (
          <>
            <div className="stat-row">
              <div>{rescueProgress.phase}</div>
              <div className="stat-value dim small">
                {rescueProgress.done}/{rescueProgress.total}
                {rescueProgress.message ? ` · ${rescueProgress.message}` : ''}
              </div>
            </div>
            <div className="bar">
              <div
                className="bar-fill"
                style={{
                  width: `${
                    rescueProgress.total > 0
                      ? (rescueProgress.done / rescueProgress.total) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </>
        )}
        {rescueLog.length > 0 && (
          <div
            ref={rescueLogRef}
            style={{
              marginTop: 8,
              maxHeight: 180,
              overflowY: 'auto',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
              fontSize: 12,
              opacity: 0.85,
              whiteSpace: 'pre-wrap',
              padding: 8,
              background: 'rgba(0,0,0,0.2)',
              borderRadius: 4,
            }}
          >
            {rescueLog.join('\n')}
          </div>
        )}
      </section>

      <section className="card">
        <h2>Models</h2>
        <label>
          <div className="label">Anthropic model</div>
          <select
            value={settings.model}
            onChange={(e) => setSettings({ model: e.target.value })}
          >
            {!ANTHROPIC_MODEL_OPTIONS.some((o) => o.id === settings.model) && (
              <option value={settings.model}>{settings.model} (custom)</option>
            )}
            {ANTHROPIC_MODEL_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className="label">TTS model</div>
          <select
            value={settings.ttsModel}
            onChange={(e) => setSettings({ ttsModel: e.target.value })}
          >
            {!TTS_MODEL_OPTIONS.some((o) => o.id === settings.ttsModel) && (
              <option value={settings.ttsModel}>
                {settings.ttsModel} (custom)
              </option>
            )}
            {TTS_MODEL_OPTIONS.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label>
          <div className="label">Target story length (words)</div>
          <input
            type="number"
            min={200}
            max={4000}
            value={settings.targetWords}
            onChange={(e) => setSettings({ targetWords: Number(e.target.value) || 800 })}
          />
        </label>
      </section>

      <section className="card">
        <h2>Storage</h2>
        {!stats && <div className="dim small">Loading…</div>}
        {stats && (
          <>
            {haveQuota ? (
              <>
                <div className="stat-row">
                  <div>Used</div>
                  <div className="stat-value strong">
                    {fmtBytes(stats.estimateUsage!)}{' '}
                    <span className="dim">of</span> {fmtBytes(stats.estimateQuota!)}{' '}
                    <span className="dim">({quotaPct!.toFixed(1)}%)</span>
                  </div>
                </div>
                <div className="bar bar-tall">
                  <div
                    className={`bar-fill ${quotaPct! > 85 ? 'bar-fill-danger' : quotaPct! > 65 ? 'bar-fill-warn' : ''}`}
                    style={{ width: `${Math.max(quotaPct!, 0.5)}%` }}
                  />
                </div>
              </>
            ) : (
              <>
                <div className="stat-row">
                  <div>Used (our measurement)</div>
                  <div className="stat-value strong">{fmtBytes(ourBytes)}</div>
                </div>
                <div className="dim small">
                  Browser didn't expose a quota number. iOS PWAs typically allow
                  hundreds of MB to ~1 GB depending on free disk space; if generation
                  ever starts failing with quota errors, that's the wall.
                </div>
              </>
            )}

            <div className="stat-row">
              <div>Breakdown</div>
              <div className="stat-value dim small">
                audio · text · app state
              </div>
            </div>
            <div className="bar bar-tall">
              <div
                className="bar-seg bar-seg-audio"
                style={{ width: `${audioPct}%` }}
                title={`Audio · ${fmtBytes(stats.audio.bytes)}`}
              />
              <div
                className="bar-seg bar-seg-text"
                style={{ width: `${textPct}%` }}
                title={`Prose + outlines · ${fmtBytes(stats.prose.bytes + stats.outlines.bytes)}`}
              />
              <div
                className="bar-seg bar-seg-ls"
                style={{ width: `${lsPct}%` }}
                title={`localStorage · ${fmtBytes(stats.localStorageBytes)}`}
              />
            </div>

            <div className="stat-row">
              <div>Audio</div>
              <div className="stat-value">
                {stats.audio.count} {stats.audio.count === 1 ? 'file' : 'files'} ·{' '}
                {fmtBytes(stats.audio.bytes)}
              </div>
            </div>
            <div className="stat-row">
              <div>Prose + outlines</div>
              <div className="stat-value">
                {fmtBytes(stats.prose.bytes + stats.outlines.bytes)}
              </div>
            </div>
            <div className="stat-row">
              <div>App state (localStorage)</div>
              <div className="stat-value">{fmtBytes(stats.localStorageBytes)}</div>
            </div>
          </>
        )}
        <div className="row">
          <button onClick={refreshStats} disabled={statsBusy}>
            {statsBusy ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      <section className="card">
        <h2>API costs</h2>
        <div className="dim small">
          Tracked since {new Date(usage.since).toLocaleString()}. ElevenLabs $/char rates
          are rough Creator-tier estimates; treat as an order-of-magnitude.
        </div>
        <div className="stat-row">
          <div>Total estimated cost</div>
          <div className="stat-value strong">
            {fmtUsd(usage.anthropic.cost + usage.eleven.cost)}
          </div>
        </div>

        <div className="cost-block">
          <div className="cost-block-header">
            Anthropic · {fmtUsd(usage.anthropic.cost)} · {fmtNum(usage.anthropic.calls)}{' '}
            {usage.anthropic.calls === 1 ? 'call' : 'calls'}
          </div>
          {usage.anthropic.calls > 0 && (
            <>
              <div className="dim small">
                {fmtNum(usage.anthropic.inputTokens)} input ·{' '}
                {fmtNum(usage.anthropic.outputTokens)} output{' '}
                {(usage.anthropic.cacheCreationTokens > 0 ||
                  usage.anthropic.cacheReadTokens > 0) && (
                  <>
                    · cache: {fmtNum(usage.anthropic.cacheCreationTokens)} write /{' '}
                    {fmtNum(usage.anthropic.cacheReadTokens)} read
                  </>
                )}
              </div>
              {Object.entries(usage.anthropic.byModel).map(([model, m]) => (
                <div key={model} className="cost-row">
                  <code className="cost-row-name">{model}</code>
                  <span className="dim small">
                    {fmtNum(m.calls)} · {fmtNum(m.inputTokens)} in / {fmtNum(m.outputTokens)} out
                  </span>
                  <span className="cost-row-amt">{fmtUsd(m.cost)}</span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="cost-block">
          <div className="cost-block-header">
            ElevenLabs · {fmtUsd(usage.eleven.cost)} · {fmtNum(usage.eleven.calls)}{' '}
            {usage.eleven.calls === 1 ? 'call' : 'calls'}
          </div>
          {usage.eleven.calls > 0 && (
            <>
              <div className="dim small">{fmtNum(usage.eleven.chars)} characters</div>
              {Object.entries(usage.eleven.byModel).map(([model, m]) => (
                <div key={model} className="cost-row">
                  <code className="cost-row-name">{model}</code>
                  <span className="dim small">
                    {fmtNum(m.calls)} · {fmtNum(m.chars)} chars
                  </span>
                  <span className="cost-row-amt">{fmtUsd(m.cost)}</span>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="row">
          <button className="ghost" onClick={onResetUsage}>
            Reset stats
          </button>
        </div>
      </section>

      <section className="card">
        <h2>Archive</h2>
        <button onClick={() => nav('/archive')}>View archived</button>
      </section>

      <section className="card">
        <h2>Debug</h2>
        <div className="dim small">
          Captures console output and uncaught errors. Useful when something goes
          wrong and there's no dev tools handy.
        </div>
        <button onClick={() => nav('/log')}>View log</button>
      </section>
    </div>
  );
}
